import React, { useState } from 'react';
import axios from 'axios';
import { environment } from '../../../environments/environment';
import { Table, TableBody, TableCell, TableContainer, TableRow, Paper, Tooltip, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import VisualizationResolver from '../visualizations/VisualizationResolver';
import CombinedNetworkGraph from '../visualizations/networkGraph/CombinedNetworkGraph';
import { combinedNetworkAnchor, isNetworkGraphItem } from '../visualizations/networkGraph/networkGraphModel';
import { Item, PAGING_KEYS, RUN_BOOKKEEPING_KEYS, UserReviewOption } from '../../services/campaignInsightsService';
import { dsVar, ink } from '../../theme/tokens';
import { tableCellPadSx, tableCellTypeSx } from '../../theme/dataTable';

/*
 * Renderers for a capsule's flow output items, shared by the Insights summary
 * table and the group drawer. Kept at module scope: declared inside a page
 * component they become a new component type on every render, and React
 * remounts them — dropping a half-written review note or a submitted decision.
 */

export const safeValue = (val: any): React.ReactNode => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'object') {
        if (Array.isArray(val)) {
            return val.map((item, idx) => {
                const rendered = typeof item === 'object' && item !== null
                    ? JSON.stringify(item)
                    : String(item ?? '');
                return (
                    <span key={idx}>
                        {rendered}{idx < val.length - 1 ? ', ' : ''}
                    </span>
                );
            });
        }
        if (val.visualizationtype || val.visualizationType) return '';
        return JSON.stringify(val);
    }
    return String(val);
};

export const isPrimitiveEntry = (val: any): boolean => {
    if (val === null || val === undefined || val === '') return false;
    return typeof val !== 'object';
};

// Distinctive markdown markers. Deliberately conservative — plain values
// (numbers, ids, sentences with snake_case words) must not be treated as
// markdown, so bare underscores/dashes on their own don't qualify.
const MARKDOWN_PATTERNS: RegExp[] = [
    /\*\*[^*\n]+\*\*/,              // **bold**
    /(^|\s)\*[^\s*][^*\n]*\*(\s|$|[.,;:!?)])/, // *italic*
    /(^|\s)_[^\s_][^_\n]*_(\s|$|[.,;:!?)])/,   // _italic_
    /`[^`\n]+`/,                    // `code`
    /^\s{0,3}#{1,6}\s+\S/m,         // # heading
    /^\s{0,3}[-*+]\s+\S/m,          // - bullet
    /^\s{0,3}\d+\.\s+\S/m,          // 1. ordered item
    /^\s{0,3}>\s+\S/m,              // > blockquote
    /^\s{0,3}\|.*\|\s*$/m,          // | table | row |
    /^\s{0,3}(?:-{3,}|\*{3,}|_{3,})\s*$/m, // horizontal rule
    /\[[^\]\n]+\]\([^)\s]+\)/,      // [link](url)
];

export const isMarkdownValue = (val: any): boolean =>
    typeof val === 'string' && MARKDOWN_PATTERNS.some(re => re.test(val));

// Markdown block/inline styling matched to the surrounding dictionary table cell.
const entryMarkdownComponents = {
    p: ({ children }: { children?: React.ReactNode }) => (
        <p style={{ margin: '0 0 8px', lineHeight: 1.65 }}>{children}</p>
    ),
    strong: ({ children }: { children?: React.ReactNode }) => (
        <strong style={{ fontWeight: 700, color: dsVar('fg') }}>{children}</strong>
    ),
    em: ({ children }: { children?: React.ReactNode }) => (
        <em style={{ fontStyle: 'italic', color: dsVar('fg-2') }}>{children}</em>
    ),
    h1: ({ children }: { children?: React.ReactNode }) => (
        <div style={{ fontSize: 14, fontWeight: 700, color: dsVar('fg'), margin: '12px 0 6px' }}>{children}</div>
    ),
    h2: ({ children }: { children?: React.ReactNode }) => (
        <div style={{ fontSize: 13, fontWeight: 700, color: dsVar('fg'), margin: '12px 0 6px' }}>{children}</div>
    ),
    h3: ({ children }: { children?: React.ReactNode }) => (
        <div style={{ fontSize: 13, fontWeight: 600, color: dsVar('fg-body'), margin: '10px 0 6px' }}>{children}</div>
    ),
    ul: ({ children }: { children?: React.ReactNode }) => (
        <ul style={{ margin: '0 0 8px', paddingLeft: 18, listStyleType: 'disc' }}>{children}</ul>
    ),
    ol: ({ children }: { children?: React.ReactNode }) => (
        <ol style={{ margin: '0 0 8px', paddingLeft: 18, listStyleType: 'decimal' }}>{children}</ol>
    ),
    li: ({ children }: { children?: React.ReactNode }) => (
        <li style={{ marginBottom: 4, lineHeight: 1.6 }}>{children}</li>
    ),
    code: ({ children }: { children?: React.ReactNode }) => (
        <code style={{
            fontFamily: '"JetBrains Mono", monospace', fontSize: 11,
            background: dsVar('page'), border: `1px solid ${dsVar('line')}`, borderRadius: 4,
            padding: '1px 5px', color: dsVar('accent-fg'),
        }}>{children}</code>
    ),
    pre: ({ children }: { children?: React.ReactNode }) => (
        <pre style={{
            margin: '0 0 8px', padding: 10, background: dsVar('page'),
            border: `1px solid ${dsVar('line')}`, borderRadius: 8,
            overflowX: 'auto', fontSize: 11,
        }}>{children}</pre>
    ),
    blockquote: ({ children }: { children?: React.ReactNode }) => (
        <blockquote style={{
            margin: '0 0 8px', padding: '2px 0 2px 10px',
            borderLeft: `2px solid ${dsVar('line')}`, color: dsVar('fg-2'),
        }}>{children}</blockquote>
    ),
    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
        <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: dsVar('accent-fg'), textDecoration: 'underline' }}>
            {children}
        </a>
    ),
    hr: () => <hr style={{ border: 0, borderTop: `1px solid ${dsVar('line')}`, margin: '10px 0' }} />,
    table: ({ children }: { children?: React.ReactNode }) => (
        <div style={{ overflowX: 'auto', marginBottom: 8 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>{children}</table>
        </div>
    ),
    th: ({ children }: { children?: React.ReactNode }) => (
        <th style={{ border: `1px solid ${dsVar('line')}`, padding: '4px 8px', textAlign: 'left', color: dsVar('fg-2'), fontWeight: 600 }}>{children}</th>
    ),
    td: ({ children }: { children?: React.ReactNode }) => (
        <td style={{ border: `1px solid ${dsVar('line')}`, padding: '4px 8px' }}>{children}</td>
    ),
};

const MarkdownEntryValue = ({ text }: { text: string }) => (
    <div style={{ fontSize: 13, color: dsVar('fg-body') }}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={entryMarkdownComponents}>
            {text}
        </ReactMarkdown>
    </div>
);

/* ── User Review Panel (compact inline) ─────────────────────────── */
const UserReviewPanel = ({
    options,
    sessionId,
    docUID,
    onSubmitted,
}: {
    options: UserReviewOption[];
    sessionId: string;
    docUID?: string;
    /** Told once a decision is recorded, so a list showing the review status can refresh. */
    onSubmitted?: () => void;
}) => {
    const currentOption = options.find(o => o.current);
    const [selectedId, setSelectedId] = useState<string>(currentOption?.id ?? options[0]?.id ?? '');
    const [reviewNotes, setReviewNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState<'approved' | 'overridden' | null>(null);
    const [error, setError] = useState<string | null>(null);

    const isCurrentSelection = selectedId === currentOption?.id;
    // Overriding the agent decision requires a written justification.
    const canOverride = !isCurrentSelection && reviewNotes.trim().length > 0;

    const submitDecision = async (payload: { approved?: boolean; override_tier?: string }) => {
        setSubmitting(true);
        setError(null);
        try {
            // Reviewer identity is stored at login (see LoginAccount).
            const reviewedBy = localStorage.getItem('user_email');
            const notes = reviewNotes.trim();
            const body = {
                ...payload,
                ...(reviewedBy ? { reviewed_by: reviewedBy } : {}),
                ...(notes ? { review_notes: notes } : {}),
            };
            await axios.post(
                `${environment.controlplaneUrl}/agent/flow/review/${encodeURIComponent(sessionId)}/decision`,
                body,
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            setSubmitted(payload.approved ? 'approved' : 'overridden');
            onSubmitted?.();
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to submit decision. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    /* ── Post-submission chip ── */
    if (submitted) {
        const isApproved = submitted === 'approved';
        const accentColor = isApproved ? ink('#10B981') : dsVar('accent-fg');
        return (
            <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '5px 12px', borderRadius: 20,
                background: isApproved
                    ? 'rgba(16,185,129,0.12)'
                    : 'rgba(255,222,89,0.12)',
                border: `1px solid ${isApproved ? 'rgba(16,185,129,0.35)' : 'rgba(255,222,89,0.35)'}`,
                whiteSpace: 'nowrap', flexShrink: 0,
            }}>
                <CheckCircleIcon style={{ color: accentColor, fontSize: 14 }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: accentColor }}>
                    {isApproved ? 'Approved' : 'Overridden'}
                </span>
                <span style={{ fontSize: 10, color: dsVar('fg-muted') }}>
                    {isApproved
                        ? (currentOption?.label ?? selectedId)
                        : (options.find(o => o.id === selectedId)?.label ?? selectedId)}
                </span>
            </div>
        );
    }

    /* ── Inline review controls ── */
    return (
        <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0,
            position: 'relative',
        }}>
            {/* Wraps so the controls fit the group drawer's width. */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 8 }}>
                {/* Label */}
                <Tooltip title="User Review Required – approve or override the agent-assigned option">
                    <div style={{
                        width: 22, height: 22, borderRadius: 6,
                        background: 'rgba(255,222,89,0.15)', border: '1px solid rgba(255,222,89,0.3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        cursor: 'help',
                    }}>
                        <EditNoteIcon style={{ color: dsVar('accent-fg'), fontSize: 13 }} />
                    </div>
                </Tooltip>

                {/* Dropdown */}
                <select
                    value={selectedId}
                    onChange={e => setSelectedId(e.target.value)}
                    disabled={submitting}
                    style={{
                        backgroundColor: dsVar('page'),
                        color: dsVar('fg-body'),
                        border: `1px solid ${dsVar('line')}`,
                        borderRadius: 8,
                        padding: '5px 10px',
                        fontSize: 11,
                        fontFamily: 'inherit',
                        outline: 'none',
                        cursor: submitting ? 'not-allowed' : 'pointer',
                        appearance: 'auto',
                        maxWidth: 220,
                    }}
                >
                    {options.map(opt => (
                        <option key={opt.id} value={opt.id}>
                            {opt.label}{opt.current ? ' ✦' : ''}
                        </option>
                    ))}
                </select>

                {/* Approve button */}
                <Tooltip title={!isCurrentSelection ? 'Select the agent-assigned option to approve' : 'Approve agent decision'}>
                    <span>{/* wrapper so Tooltip works on disabled button */}
                        <button
                            onClick={() => submitDecision({ approved: true })}
                            disabled={submitting || !isCurrentSelection}
                            style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                                padding: '5px 12px', borderRadius: 8, border: 'none',
                                cursor: (!submitting && isCurrentSelection) ? 'pointer' : 'not-allowed',
                                background: isCurrentSelection
                                    ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                                    : dsVar('raised'),
                                color: isCurrentSelection ? '#fff' : dsVar('fg-muted'),
                                fontWeight: 700, fontSize: 10,
                                fontFamily: 'inherit',
                                textTransform: 'uppercase', letterSpacing: '0.05em',
                                opacity: submitting ? 0.6 : 1,
                                transition: 'all 0.2s', whiteSpace: 'nowrap',
                                boxShadow: isCurrentSelection ? '0 2px 8px rgba(16,185,129,0.25)' : 'none',
                            }}
                        >
                            <CheckCircleIcon style={{ fontSize: 13 }} />
                            Approve
                        </button>
                    </span>
                </Tooltip>

                {/* Override button */}
                <Tooltip title={
                    isCurrentSelection
                        ? 'Select a different option to override'
                        : (canOverride
                            ? `Override with ${options.find(o => o.id === selectedId)?.label ?? selectedId}`
                            : 'Add review notes explaining the override')
                }>
                    <span>
                        <button
                            onClick={() => submitDecision({ override_tier: selectedId })}
                            disabled={submitting || !canOverride}
                            style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                                padding: '5px 12px', borderRadius: 8, border: 'none',
                                cursor: (!submitting && canOverride) ? 'pointer' : 'not-allowed',
                                background: canOverride
                                    ? 'linear-gradient(135deg, #FFDE59 0%, #f0c400 100%)'
                                    : dsVar('raised'),
                                color: canOverride ? dsVar('on-accent') : dsVar('fg-muted'),
                                fontWeight: 700, fontSize: 10,
                                fontFamily: 'inherit',
                                textTransform: 'uppercase', letterSpacing: '0.05em',
                                opacity: submitting ? 0.6 : 1,
                                transition: 'all 0.2s', whiteSpace: 'nowrap',
                                boxShadow: canOverride ? '0 2px 8px rgba(255,222,89,0.25)' : 'none',
                            }}
                        >
                            <EditNoteIcon style={{ fontSize: 13 }} />
                            Override
                        </button>
                    </span>
                </Tooltip>

                {submitting && <CircularProgress size={14} thickness={4} sx={{ color: dsVar('accent-fg'), flexShrink: 0 }} />}

                {/* Error tooltip */}
                {error && (
                    <Tooltip title={error}>
                        <span style={{ fontSize: 10, color: dsVar('danger'), cursor: 'help', flexShrink: 0 }}>⚠</span>
                    </Tooltip>
                )}
            </div>

            {/* Review notes — shown once a different option is selected (the override path), where they are mandatory */}
            {!isCurrentSelection && (
                <textarea
                    value={reviewNotes}
                    onChange={e => setReviewNotes(e.target.value)}
                    disabled={submitting}
                    rows={2}
                    required
                    placeholder={`Why override with ${options.find(o => o.id === selectedId)?.label ?? selectedId}? (required)`}
                    style={{
                        width: '100%', boxSizing: 'border-box',
                        backgroundColor: dsVar('page'),
                        color: dsVar('fg-body'),
                        border: `1px solid ${canOverride ? dsVar('line') : 'rgba(255,222,89,0.45)'}`,
                        borderRadius: 8,
                        padding: '6px 10px',
                        fontSize: 11,
                        fontFamily: 'inherit',
                        lineHeight: 1.5,
                        outline: 'none',
                        resize: 'vertical',
                        opacity: submitting ? 0.6 : 1,
                    }}
                />
            )}
        </div>
    );
};

/**
 * The review an item asks for: its userReviewOptions, and the userReviewThreadId
 * used as the session ID for the decision API. Either item type can carry them —
 * a DICTIONARY among its fields, a MULTICOLUMNDICT (an EDD referral's case
 * overview) alongside its table — so both are read the same way.
 */
const reviewRequestOf = (item: Item): { options: UserReviewOption[]; sessionId: string } | null => {
    const entries = item.entries ?? [];
    const options = entries.find(e => e.key === 'userReviewOptions')?.value;
    const thread = entries.find(e => e.key === 'userReviewThreadId')?.value;
    if (!Array.isArray(options) || options.length === 0) return null;
    if (thread === null || thread === undefined || thread === '') return null;
    return { options: options as UserReviewOption[], sessionId: String(thread) };
};

const reviewPanelFor = (item: Item, onReviewSubmitted?: () => void) => {
    const review = reviewRequestOf(item);
    return review && (
        // Keyed by review thread so the panel's decision state
        // never outlives the case it was submitted for.
        <UserReviewPanel
            key={review.sessionId}
            options={review.options}
            sessionId={review.sessionId}
            docUID={item.docUID}
            onSubmitted={onReviewSubmitted}
        />
    );
};

// `groupKey` scopes the element keys to the group being viewed. Without it,
// items from different groups share the same index-based key and React
// reuses the mounted subtree — carrying a submitted review decision over to
// the next group's case.
const renderItem = (item: Item, index: number, groupKey = '', onReviewSubmitted?: () => void) => {
    if (item.objectType === 'DICTIONARY' && item.entries) {
        const titleEntry = item.entries.find(e => e.label.toLowerCase() === 'title');
        const reviewPanel = reviewPanelFor(item, onReviewSubmitted);

        // Exclude review-related keys, the identifiers used only to fetch the group or insight, run
        // bookkeeping, and paging bookkeeping, from the rendered table
        const EXCLUDED_KEYS = new Set([
            'userReviewOptions', 'userReviewSessionId', 'userReviewThreadId', 'userReviewAction',
            'groupIdentifier', 'insightId', ...RUN_BOOKKEEPING_KEYS, ...PAGING_KEYS,
        ]);
        const renderableEntries = item.entries.filter(e =>
            e.label.toLowerCase() !== 'title' &&
            !EXCLUDED_KEYS.has(e.key as string) &&
            isPrimitiveEntry(e.value)
        );
        if (renderableEntries.length === 0 && !titleEntry && !reviewPanel) return null;
        return (
            <div key={`${groupKey}-${index}`} className="mb-8">
                {/* Title row with inline review panel at top-right */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                    {titleEntry ? (
                        <h4 className="text-[15px] font-semibold text-fg" style={{ margin: 0 }}>
                            {String(titleEntry.value)}
                        </h4>
                    ) : <div />}
                    {reviewPanel}
                </div>
                {renderableEntries.length > 0 && (
                    <TableContainer component={Paper} sx={{ backgroundColor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, borderRadius: '12px', boxShadow: 'none' }}>
                        <Table size="small">
                            <TableBody>
                                {renderableEntries.map((entry, idx) => (
                                    <TableRow key={idx} sx={{ '& td, & th': { borderBottom: `1px solid ${dsVar('line')}` }, '&:last-child td, &:last-child th': { borderBottom: 'none' } }}>
                                        <TableCell sx={{ ...tableCellPadSx, color: dsVar('fg-2'), fontWeight: 600, width: '40%', borderRight: `1px solid ${dsVar('line')}`, fontFamily: '"JetBrains Mono", monospace', fontSize: '12px', verticalAlign: 'top' }}>{entry.label}</TableCell>
                                        <TableCell sx={{ ...tableCellTypeSx, ...tableCellPadSx, verticalAlign: 'top' }}>
                                            {isMarkdownValue(entry.value)
                                                ? <MarkdownEntryValue text={String(entry.value)} />
                                                : safeValue(entry.value)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </div>
        );
    }
    if (item.objectType === 'MULTICOLUMNDICT' && item.value) {
        const { headers, data, title } = item.value;
        const combinedMetadata = { ...(item.metadata || {}), ...(item.value.metadata || {}) };
        const reviewPanel = reviewPanelFor(item, onReviewSubmitted);
        return (
            <div key={`${groupKey}-${index}`} className="mb-8">
                {(title || reviewPanel) && (
                    // Title row with inline review panel at top-right, as for a dictionary
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                        {title ? (
                            <h4 className="text-[15px] font-semibold text-fg" style={{ margin: 0 }}>
                                {String(title)}
                            </h4>
                        ) : <div />}
                        {reviewPanel}
                    </div>
                )}
                <VisualizationResolver
                    headers={headers}
                    data={data}
                    metadata={combinedMetadata}
                    itemLabel={item.label}
                    safeValue={safeValue}
                />
            </div>
        );
    }
    return null;
};

/**
 * Every item of one group. When the group carries several network visuals they
 * can also be viewed merged into one graph, shown where the last of them sits in
 * the group's order (see combinedNetworkAnchor), not above the whole report.
 */
export const InsightGroupItems: React.FC<{
    items: Item[];
    /** Scopes element keys to the group, so no mounted state carries across groups. */
    groupKey: string;
    onReviewSubmitted?: () => void;
}> = ({ items, groupKey, onReviewSubmitted }) => {
    const [networkView, setNetworkView] = useState<'combined' | 'individual'>('combined');
    const networkItems = items.filter(isNetworkGraphItem);
    const anchor = combinedNetworkAnchor(items);
    const combined = anchor >= 0 && networkView === 'combined';
    const networkControls = (
        <>
            <div className="inline-flex rounded-lg border border-line overflow-hidden mb-6" role="group" aria-label="Network view">
                {(['combined', 'individual'] as const).map(view => (
                    <button
                        key={view}
                        type="button"
                        aria-pressed={networkView === view}
                        onClick={() => setNetworkView(view)}
                        className={`px-3 py-1.5 text-xs font-semibold border-none cursor-pointer ${networkView === view ? 'bg-accent text-on-accent' : 'bg-transparent text-fg-2 hover:text-fg-body'}`}
                    >
                        {view === 'combined' ? 'Combined network' : 'Individual patterns'}
                    </button>
                ))}
            </div>
            {combined && (
                <div className="mb-8">
                    <CombinedNetworkGraph key={groupKey} items={networkItems} safeValue={safeValue} />
                </div>
            )}
        </>
    );
    return (
        <>
            {items.map((item, index) => {
                const rendered = combined && isNetworkGraphItem(item) ? null : renderItem(item, index, groupKey, onReviewSubmitted);
                return index === anchor
                    ? <React.Fragment key={`${groupKey}-network-view`}>{networkControls}{rendered}</React.Fragment>
                    : rendered;
            })}
        </>
    );
};
